/**
 * Consignaciones — pantalla unificada del módulo de consignaciones.
 *
 * La vista principal es el listado de conduces consignados embebido; cada
 * fila abre su detalle a pantalla completa (mismo flujo que Ventas x Mayor).
 * Tarjetas de acción (diálogos con barra Volver):
 *   - "Estado de cuenta del consignante": en piso vs. por pagar.
 *   - "Reporte de consignaciones": consignados, vendidos y devueltos por
 *     rango de fechas.
 *
 * Aquí NO se paga. En cuanto un motor consignado se vende, su conduce
 * aparece en Cuentas por Pagar valiendo solo lo vendido, y se le paga desde
 * ahí como a cualquier suplidor: pagar es tesorería (ares8515). Lo que se
 * hace en este módulo es consultar y devolver al consignante los motores
 * que sigan en piso, que son suyos y no se le deben.
 */
import React, { useState } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Dialog from "@mui/material/Dialog";
import Typography from "@mui/material/Typography";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import AssignmentReturnOutlinedIcon from "@mui/icons-material/AssignmentReturnOutlined";
import { useMaterialUIController } from "context";
import { tienePermisosModulo } from "lib/seguridad/permisosPorModulo";
import { FP, PageHeader, ActionCard, BarraVolver } from "components/financepro";
import MantenimientoConsignaciones from "modules/motores/Consignaciones/mantenimientoconsignaciones";
import ReporteConsignaciones from "modules/motores/Consignaciones/reporteconsignaciones";
import EstadoCuentaConsignante from "modules/motores/Consignaciones/estadocuentaconsignante";
import DevolucionesConsignaciones from "modules/motores/Consignaciones/devolucionesconsignaciones";
import useAccionInicial from "lib/useAccionInicial";

export default function Consignaciones() {
  const [reporteOpen, setReporteOpen] = useState(false);
  const [estadoOpen, setEstadoOpen] = useState(false);
  const [devolucionesOpen, setDevolucionesOpen] = useState(false);

  // Los contenedores de CxP ya hacían este chequeo; aquí faltaba y el rol
  // sin permiso veía la pantalla completa antes de comerse el 403.
  const [controller] = useMaterialUIController();
  const tieneAcceso = tienePermisosModulo(controller.roleDetalles, "consignaciones");

  // Enlace profundo desde el buscador global (?accion=...).
  useAccionInicial({
    reporte: { abrir: () => setReporteOpen(true) },
    estadocuenta: { abrir: () => setEstadoOpen(true) },
    devoluciones: { abrir: () => setDevolucionesOpen(true) },
  });

  const [refreshKey] = useState(0);

  if (!tieneAcceso) {
    return (
      <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
        <PageHeader title="Consignaciones" subtitle="Motores recibidos en consignación" />
        <Box sx={{ p: { xs: 2, md: 3 } }}>
          <Typography sx={{ color: FP.color.textSecondary, fontWeight: 700 }}>
            Su rol no tiene acceso a Consignaciones.
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title="Consignaciones"
        subtitle="Motores recibidos en consignación; lo vendido se paga desde Cuentas por Pagar"
      />

      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Grid container spacing={2.5} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={4}>
            <ActionCard
              icon={ReceiptLongOutlinedIcon}
              titulo="Estado de cuenta del consignante"
              descripcion="Lo que está en piso frente a lo que ya se le debe"
              color={FP.color.navy}
              onClick={() => setEstadoOpen(true)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <ActionCard
              icon={AssessmentOutlinedIcon}
              titulo="Reporte de consignaciones"
              descripcion="Consignados, vendidos y devueltos por rango de fechas"
              color={FP.color.accentBlue}
              onClick={() => setReporteOpen(true)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <ActionCard
              icon={AssignmentReturnOutlinedIcon}
              titulo="Devoluciones"
              descripcion="Devoluciones registradas: reimpresión de conduces y reingresos"
              color={FP.color.danger}
              onClick={() => setDevolucionesOpen(true)}
            />
          </Grid>
        </Grid>

        {/* Vista principal: listado de conduces; el detalle se abre por fila */}
        <MantenimientoConsignaciones embedded refreshToken={refreshKey} />
      </Box>

      {/* Estado de cuenta del consignante */}
      <Dialog fullScreen open={estadoOpen} onClose={() => setEstadoOpen(false)}>
        <BarraVolver onExit={() => setEstadoOpen(false)} />
        <EstadoCuentaConsignante />
      </Dialog>

      {/* Reporte (pantalla existente, con barra Volver) */}
      <Dialog fullScreen open={reporteOpen} onClose={() => setReporteOpen(false)}>
        <BarraVolver onExit={() => setReporteOpen(false)} />
        <ReporteConsignaciones />
      </Dialog>

      {/* Devoluciones registradas: reimpresión y reingreso */}
      <Dialog fullScreen open={devolucionesOpen} onClose={() => setDevolucionesOpen(false)}>
        <BarraVolver onExit={() => setDevolucionesOpen(false)} />
        <DevolucionesConsignaciones />
      </Dialog>
    </Box>
  );
}
